import {useFirstPageCheck, useRouteNavigator} from "@vkontakte/vk-mini-apps-router";
import {
    Avatar,
    Button,
    Card,
    Cell,
    Header,
    PanelHeader,
    PanelHeaderBack,
    Spacing,
    Spinner,
    Tabs,
    TabsItem
} from '@vkontakte/vkui';
import React, {useEffect, useState} from 'react';
import {getWeeklyPlayerRating} from "../../services/player";
import BackgroundLetters from "../../components/Background/Background";
import {openTelegramShare} from "../../services/telegramWebApp";

// Общие стили для карточек рейтинга
const ratingCardClass = "w-full bg-gray-800 text-gray-400 border border-gray-700/40";

function Rating() {
    const routeNavigator = useRouteNavigator();
    const isFirstPage = useFirstPageCheck();

    const handleBackButton = () => {
        return isFirstPage ? routeNavigator.push('/') : routeNavigator.back();
    }

    return (
        <>
            <BackgroundLetters/>

            <PanelHeader
                before={<PanelHeaderBack className={"[&>svg]:p-0"} onClick={handleBackButton}/>}
                separator={null}
                className={"sticky top-0 z-20 text-gray-400"}
            >
                Rating
            </PanelHeader>

            <div className={"relative z-10 flex flex-col p-4 items-center mx-auto w-full max-w-lg text-gray-400"}>
                {/* Общий ограниченный контентный блок повторяет ширину max-w-lg главной страницы */}
                <Spacing size={12}/>
                <div className={"w-full"}>
                    <PlayerList/>
                </div>
                <Spacing size={32}/>
            </div>
        </>
    )
}

function PlayerList() {
    // Состояние для общего рейтинга
    const [players, setPlayers] = useState([]);
    const [currentPlayer, setCurrentPlayer] = useState({});
    const [currentPlayerRank, setCurrentPlayerRank] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    // Состояние для рейтинга друзей
    const [friends, setFriends] = useState([]);
    const [friendsLoading, setFriendsLoading] = useState(false);

    // Состояние для вкладок (табов)
    const [activeTab, setActiveTab] = useState('overall');

    // Загрузка общего рейтинга при монтировании компонента
    useEffect(() => {
        getWeeklyPlayerRating().then(resp => {
            const data = resp.data.response;
            setCurrentPlayer(data.player);
            setCurrentPlayerRank(data.player_rank);
            setPlayers(data.winners || []);
        }).catch(err => {
            console.log("getTopWinners error:", err);
        }).finally(() => {
            setIsLoading(false);
        });
    }, []);

    // Загрузка рейтинга друзей при переключении на вкладку
    useEffect(() => {
        if (activeTab === 'friends' && friends.length === 0) {
            fetchFriendsRating();
        }
    }, [activeTab]);

    const fetchFriendsRating = () => {
        setFriendsLoading(true);
        Promise.resolve().then(() => {
            setFriends([]);
        }).catch(err => {
            console.error("Failed to fetch friends rating:", err);
        }).finally(() => {
            setFriendsLoading(false);
        });
    };

    const handleInvite = () => {
        openTelegramShare({
            text: "Join Wordmania in Telegram",
            url: "https://t.me",
        });
    };

    const renderOverallRating = () => (
        <>
            {isLoading ? <Spinner size={"large"}/> : (
                <>
                    {players.length > 0 ? (
                        <Card className={ratingCardClass}>
                            <Spacing size={12}/>
                            <p className={"text-xs text-gray-400 px-4"}>Weekly leaderboard</p>
                            <Spacing size={12}/>
                            {players.map(player => (
                                <Cell before={<Avatar src={player.avatar}/>}
                                      key={player.id}
                                      subtitle={`Points: ${player.weekly_score || 0} | Games: ${player.weekly_games || 0}`}
                                      disabled={true}
                                      className={"vkui-cell-text"}
                                >
                                    <span className={"text-gray-400"}>{player.name}</span>
                                </Cell>
                            ))}
                            <Spacing size={12}/>
                        </Card>
                    ) : (
                        <Card className={ratingCardClass}>
                            <Header className={"text-gray-400"}>No rating data yet</Header>
                        </Card>
                    )}

                    <Spacing size={24}/>

                    <Card className={ratingCardClass}>
                        <Header subtitle={`Rank position: ${currentPlayerRank}`}>
                            <span className={"text-gray-400"}>Your rating</span>
                        </Header>
                        <Cell before={<Avatar src={currentPlayer.avatar}/>}
                              key={currentPlayer.id}
                              subtitle={`Weekly points: ${currentPlayer.weekly_score || 0} | Games: ${currentPlayer.weekly_games || 0}`}
                              disabled={true}
                              className={"vkui-cell-text"}
                        >
                            <span className={"text-gray-400"}>{currentPlayer.name}</span>
                        </Cell>
                        <Spacing size={12}/>
                    </Card>
                </>
            )}
        </>
    );

    const renderFriendsRating = () => (
        <Card className={ratingCardClass}>
            {friendsLoading ? (
                <div className={"p-4 flex justify-center items-center"}>
                    <Spinner size="large"/>
                </div>
            ) : (
                <>
                    {(friends && friends.length > 0) ? (
                        <>
                            <Spacing size={12}/>
                            {friends.map(player => (
                                <Cell before={<Avatar src={player.avatar}/>}
                                      key={player.id}
                                      subtitle={`Points: ${player.weekly_score || 0} | Games: ${player.weekly_games || 0}`}
                                      disabled={true}
                                      className={"vkui-cell-text"}
                                >
                                    <span className={"text-gray-400"}>{player.name}</span>
                                </Cell>
                            ))}
                            <Spacing size={12}/>
                        </>
                    ) : (
                        <div className={"p-4 flex flex-col items-center gap-y-3"}>
                            <p className={"text-sm text-gray-400 mb-4"}>
                                None of your friends are playing yet. Send an invite and play together!
                            </p>
                            <Button
                                size="s"
                                stretched
                                mode="primary"
                                className={"bg-yellow-400 hover:bg-yellow-500 text-gray-800 border-yellow-400"}
                                onClick={handleInvite}>
                                Send invite
                            </Button>
                        </div>
                    )}
                </>
            )}
        </Card>
    );

    const ratingContent = activeTab === 'overall' ? renderOverallRating() : renderFriendsRating();

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === 'friends' && friends.length === 0) {
            // Ставим загрузку сразу, чтобы кнопка не успевала мигнуть до спиннера.
            setFriendsLoading(true);
        }
    };

    return (
        <div className={"flex flex-col gap-4 w-full"}>
            <div className={"w-full"}>
                <Card className={ratingCardClass}>
                    <Tabs>
                        <TabsItem
                            selected={activeTab === 'overall'}
                            onClick={() => handleTabChange('overall')}
                        >
                            <span className={"text-gray-400"}>Overall</span>
                        </TabsItem>
                        <TabsItem
                            selected={activeTab === 'friends'}
                            onClick={() => handleTabChange('friends')}
                        >
                            <span className={"text-gray-400"}>Friends</span>
                        </TabsItem>
                    </Tabs>
                </Card>
            </div>

            <div className={"flex flex-col gap-4 w-full"}>
                <div className={"w-full"}>
                    {ratingContent}
                </div>
            </div>
        </div>
    );
}

export default Rating;
